<!--
  ObjectInput.vue

  Form input for object-kind fields. Renders pretty JSON in a textarea
  and round-trips through the FieldType's `parse` (which returns the
  parsed object on valid input, null on invalid).
-->
<template>
  <textarea
    class="textarea"
    :class="`is-${size}`"
    :value="formattedValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :rows="rows"
    @input="handleInput"
  />
</template>

<script setup lang="ts">
import { computed } from "vue"
import type { Field } from "@auger/core/fields"
import { useField } from "../useField"

const props = withDefaults(
  defineProps<{
    modelValue?: Record<string, unknown> | null
    field: Field
    placeholder?: string
    disabled?: boolean
    readonly?: boolean
    rows?: number
    size?: "small" | "normal" | "medium" | "large"
  }>(),
  {
    modelValue: null,
    placeholder: "",
    disabled: false,
    readonly: false,
    rows: 6,
    size: "normal",
  }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
}>()

const { handleInput } = useField(props.field, emit)

// The FieldType's `format` returns `JSON.stringify(value)` — fine for
// inline display but unreadable in a textarea. Pretty-print here instead.
const formattedValue = computed(() => {
  if (props.modelValue == null) return ""
  try {
    return JSON.stringify(props.modelValue, null, 2)
  } catch {
    return String(props.modelValue)
  }
})
</script>
